import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiX } from 'react-icons/fi';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import Pagination from '../../components/ui/Pagination';
import SortableHeader from '../../components/ui/SortableHeader';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';
import SelectLeadModal from '../../components/Calendar/SelectLeadModal';
import {
  getActiveDirectoryUsers,
  resolveDisplayName,
  useUsersDirectory,
} from '../../lib/usersDirectory';
import {
  calculateNetWorth,
  calculateEstateNeedsAttentionCount,
  calculateEstateNeedsAttentionPct,
} from '../../lib/financialAnalysisUtils';

const PAGE_SIZE = 25;

function formatCurrency(val) {
  if (val == null || Number.isNaN(Number(val))) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(val);
}

function formatDate(val) {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function leadDisplayName(lead) {
  if (!lead) return '—';
  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ').trim();
  return name || '—';
}

export default function FinancialAnalysisList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { users: directoryUsers } = useUsersDirectory();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAgent, setFilterAgent] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectLeadOpen, setSelectLeadOpen] = useState(false);
  const [startingFna, setStartingFna] = useState(false);

  const isManagerPlus = ['admin', 'super_admin', 'manager'].includes(user?.role);
  const hasActiveFilters = Boolean(search || filterAgent);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (isManagerPlus) {
        if (filterAgent) filters.created_by = filterAgent;
      } else if (user?.id) {
        filters.created_by = user.id;
      }

      const data = await api.financialAnalysis.getAll(filters);
      setRows(data || []);
      setPage(1);
    } catch {
      addToast({ type: 'error', message: 'Failed to load financial analyses' });
    } finally {
      setLoading(false);
    }
  }, [search, filterAgent, isManagerPlus, user?.id, addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const agents = useMemo(() => {
    if (!isManagerPlus) return [];
    return getActiveDirectoryUsers(directoryUsers).map((u) => ({
      id: u.id,
      name: u.full_name || 'Unknown',
    }));
  }, [isManagerPlus, directoryUsers]);

  const handleStartFnaForLead = async (lead) => {
    if (!lead?.id || !user?.agency_id) {
      addToast({ type: 'error', message: 'Lead and agency are required to start an FNA' });
      return;
    }
    setStartingFna(true);
    try {
      const existing = await api.financialAnalysis.getByLeadId(lead.id);
      if (existing?.id) {
        navigate(`/financial-analysis/${existing.id}`);
        return;
      }
      const created = await api.financialAnalysis.create({
        leadId: lead.id,
        agencyId: lead.agency_id || user.agency_id,
        userId: user.id || null,
      });
      if (!created?.id) throw new Error('Create returned no analysis');
      navigate(`/financial-analysis/${created.id}`);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not start financial analysis',
        message: err?.message,
      });
    } finally {
      setStartingFna(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'updated_at' || field === 'created_at' ? 'desc' : 'asc');
    }
  };

  const enriched = useMemo(() => {
    return (rows || []).map((row) => {
      const netWorth = calculateNetWorth(row.assets, row.liabilities);
      const estateNeedsAttention = calculateEstateNeedsAttentionCount(row.estate_checklist);
      const estatePct = calculateEstateNeedsAttentionPct(row.estate_checklist);
      const leadName = leadDisplayName(row.lead);
      const createdByName = resolveDisplayName(row.created_by, row.creator) || '—';
      return { ...row, netWorth, estateNeedsAttention, estatePct, leadName, createdByName };
    });
  }, [rows, directoryUsers]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...enriched].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'leadName') {
        aVal = a.leadName || '';
        bVal = b.leadName || '';
      } else if (sortField === 'createdByName') {
        aVal = a.createdByName || '';
        bVal = b.createdByName || '';
      } else if (sortField === 'netWorth') {
        aVal = a.netWorth;
        bVal = b.netWorth;
      } else if (sortField === 'estatePct') {
        aVal = a.estatePct;
        bVal = b.estatePct;
      }
      if (aVal == null || aVal === '') return 1;
      if (bVal == null || bVal === '') return -1;
      if (typeof aVal === 'string') return aVal.localeCompare(bVal) * dir;
      return (aVal > bVal ? 1 : aVal < bVal ? -1 : 0) * dir;
    });
  }, [enriched, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const sortProps = { sortField, sortDir, onSort: handleSort };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Financial Analysis</h1>
          <p className="text-gray-400 text-sm mt-1">
            {rows.length} analysis{rows.length !== 1 ? 'es' : ''}
          </p>
        </div>
        <button
          type="button"
          disabled={startingFna || !user?.agency_id}
          onClick={() => setSelectLeadOpen(true)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg
                     bg-[#00AAFF] text-white text-sm font-medium hover:bg-[#0090d9]
                     disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
        >
          {startingFna ? (
            <>
              <Spinner className="w-4 h-4" />
              Starting…
            </>
          ) : (
            <>
              <FiPlus className="w-4 h-4" />
              Start New FNA
            </>
          )}
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by lead name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg
                       text-sm text-white placeholder-gray-500 focus:outline-none
                       focus:border-[#00AAFF] transition-colors"
          />
        </div>
        {isManagerPlus && (
          <select
            value={filterAgent}
            onChange={(e) => setFilterAgent(e.target.value)}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm
                       text-gray-300 focus:outline-none focus:border-[#00AAFF] transition-colors"
          >
            <option value="">All Agents</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        )}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setFilterAgent('');
            }}
            className="px-3 py-2 text-xs text-gray-400 hover:text-white
                       border border-gray-700 rounded-lg transition-colors
                       flex items-center gap-1.5"
          >
            <FiX className="w-3 h-3" /> Reset
          </button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-500 text-sm">
            <Spinner className="text-primary" />
            Loading financial analyses…
          </div>
        ) : sorted.length === 0 ? (
          <EmptyState
            title="No financial analyses yet"
            description={
              hasActiveFilters
                ? 'Try adjusting your filters.'
                : 'Click Start New FNA to select or create a lead, then open the builder.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <SortableHeader label="Lead Name" field="leadName" {...sortProps} />
                    <SortableHeader label="Net Worth" field="netWorth" {...sortProps} />
                    <SortableHeader label="Needs Attention" field="estatePct" {...sortProps} />
                    <SortableHeader label="Created By" field="createdByName" {...sortProps} />
                    <SortableHeader label="Last Updated" field="updated_at" {...sortProps} />
                    <th className="px-6 py-3 font-medium text-xs uppercase tracking-wide text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 font-medium text-gray-900">
                        <Link
                          to={`/financial-analysis/${row.id}`}
                          className="text-primary hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {row.leadName}
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-gray-700 tabular-nums">
                        {formatCurrency(row.netWorth)}
                      </td>
                      <td className="px-6 py-4 text-gray-700 tabular-nums">
                        {`${row.estateNeedsAttention} of 8`}
                      </td>
                      <td className="px-6 py-4 text-gray-600">{row.createdByName}</td>
                      <td className="px-6 py-4 text-gray-500 text-xs">
                        {formatDate(row.updated_at)}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          className="text-sm font-medium text-primary hover:underline"
                          onClick={() => navigate(`/financial-analysis/${row.id}/report`)}
                        >
                          View Report
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {sorted.length > PAGE_SIZE && (
              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={sorted.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>

      <SelectLeadModal
        isOpen={selectLeadOpen}
        onClose={() => setSelectLeadOpen(false)}
        agencyId={user?.agency_id}
        title="Start New FNA"
        description="Select an existing lead, or create a new one, then continue into the financial analysis builder."
        allowQuickCreate
        onSelect={handleStartFnaForLead}
      />
    </div>
  );
}
